<template>
  <!-- RQ "Documents" card on the detail page (Android DocumentUploadFragment + AditionalDocumentUploadFragment):
       required document slots with View / Upload, then the other documents as the S&R gallery (PhotoGallery) -->
  <div class="rqCard font-roboto">
    <div class="flex items-center gap-1 pb-[15px]">
      <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
      <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
      <p class="text-[18px] laptop:text-[20px] font-semibold pl-2 m-0 text-[#333333]">Documents</p>
    </div>

    <!-- required slots -->
    <template v-if="documents.length">
      <p class="m-0 mb-[10px] text-[14px] text-[#333333]">Following documents are required to complete the service</p>
      <div class="grid grid-cols-1 tablet:grid-cols-2 gap-[12px]">
        <div v-for="doc in documents" :key="doc.id"
          class="flex items-center gap-[12px] p-[10px] rounded-[10px] border-1 border-[#EFEFEF] bg-white">
          <button type="button" :disabled="!doc.url" :aria-label="'View ' + doc.name"
            class="w-[56px] h-[56px] flex-shrink-0 p-0 rounded-[8px] border-[3px] border-[#EFEFEF] overflow-hidden flex items-center justify-center bg-[#F7F7F9] disabled:cursor-default"
            @click="view(doc)">
            <img v-if="doc.url && !isPdf(doc)" :src="doc.url" alt="" class="w-full h-full object-cover">
            <img v-else-if="doc.url" src="../../assets/images/pdf-icon.svg" alt="" class="w-[28px] h-[28px]">
            <svg v-else width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M7 3h7l5 5v13H7z M14 3v5h5" stroke="#CCCCCC" stroke-width="1.6" stroke-linejoin="round" />
            </svg>
          </button>
          <div class="flex-1 min-w-0">
            <p class="m-0 text-[14px] text-[#333333] break-words">{{ doc.name }}</p>
            <div class="flex flex-wrap items-center gap-[6px] mt-[4px] text-[12px]">
              <span class="px-[8px] py-[1px] rounded-full"
                :class="doc.url ? 'bg-[#E7F6EE] text-[#1FA971]' : 'bg-[#EFEFEF] text-[#999999]'">
                {{ doc.url ? 'Uploaded' : 'Not uploaded' }}</span>
              <span v-if="doc.is_required" class="text-[#EE5851]">*Required</span>
            </div>
            <a v-if="String(doc.name).toLowerCase() === 'heat load document'" :href="HEAT_LOAD_URL" target="_blank"
              rel="noopener" class="inline-block mt-[4px] text-[12px] text-[#7695FF] no-underline">Heat Load Calculator</a>
          </div>
          <div class="flex flex-col gap-[6px] flex-shrink-0">
            <button v-if="doc.url" type="button" class="rqDocBtn bg-[#fff] border-[#A084E8] text-[#A084E8] hover:bg-[#A084E8] hover:text-[#fff]"
              @click="view(doc)">View</button>
            <button v-if="canUploadSlot(doc)" type="button"
              class="rqDocBtn bg-[#fff] border-[#7695FF] text-[#7695FF] hover:bg-[#7695FF] hover:text-[#fff]" @click="uploadSlot(doc)">
              {{ doc.url ? 'Replace' : 'Upload' }}</button>
          </div>
        </div>
      </div>
    </template>

    <!-- other documents: S&R gallery look -->
    <div :class="documents.length ? 'mt-[20px] pt-[15px] border-t border-[#EFEFEF]' : ''">
      <p class="m-0 mb-[10px] text-[16px] font-medium text-[#333333]">Other Documents</p>
      <p class="!mb-[15px] text-[14px] text-[#333333]">
        <span class="text-[#F67280]">Note:</span> Please avoid uploading any financial documents
      </p>
      <div v-if="!readOnly" class="flex flex-wrap items-center gap-[10px] mb-[20px]">
        <button type="button"
          class="flex items-center justify-center gap-[8px] w-[170px] h-[36px] rounded-[5px] border-1 border-[#7695FF] bg-[#7695FF] text-[14px] text-white"
          @click="pick({ kind: 'add' }, true)">
          Upload documents
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M7 18a4.5 4.5 0 0 1-.6-8.96A6 6 0 0 1 18 8.5a4 4 0 0 1-.5 7.97M12 12v7m0-7-2.5 2.5M12 12l2.5 2.5"
              stroke="#FFFFFF" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <span class="text-[12px] text-[#F67280]">(Max file size to upload is 10 MB)</span>
      </div>
      <div v-if="!galleryFiles.length"
        class="flex flex-col items-center justify-center min-h-[150px] px-[15px] rounded-[10px] border-1 border-dashed border-[#CCCCCC] bg-[#EFEFEF80] text-center">
        <p class="m-0 text-[14px] leading-[20px] text-[#000000]">No documents to display</p>
      </div>
      <PhotoGallery v-else :files="galleryFiles" :deletable="!readOnly" alt-text="Document" @remove="remove" />
    </div>

    <!-- required documents open in the same viewer as the gallery (prev / next over the uploaded ones) -->
    <PhotoViewer v-model:index="viewerIndex" :files="viewerFiles" alt-text="Document" />
    <input ref="fileInput" type="file" class="hidden" accept="image/*,application/pdf" @change="onFiles">
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import PhotoGallery from "../gallery/PhotoGallery.vue";
import PhotoViewer from "../gallery/PhotoViewer.vue";
import { rqApi } from "@/services/rqApi";

const toast = useToast();
const MAX_SIZE = 10 * 1024 * 1024; // backend limit (DocumentController: max 10MB)

export default {
  components: { PhotoGallery, PhotoViewer },
  props: {
    orderId: { type: [Number, String], required: true },
    // required slots: Android disables upload once the installation is completed
    canUpload: { type: Boolean, default: true },
    readOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      HEAT_LOAD_URL: "https://www.highseer.com/pages/hvac-load-calculator",
      documents: [],
      otherDocuments: [],
      uploading: [],
      viewerIndex: null,
      target: null, // what the next file pick is for: { kind: 'slot' | 'add', doc? }
    };
  },
  computed: {
    uploadedDocs() {
      return this.documents.filter(d => d.url);
    },
    viewerFiles() {
      return this.uploadedDocs.map(d => ({ url: d.url, title: d.name, mime: this.isPdf(d) ? "pdf" : "image" }));
    },
    // PhotoGallery shape; files still uploading show a spinner tile
    galleryFiles() {
      return this.otherDocuments
        .map(d => ({ id: d.id, url: d.url, title: d.title, mime: this.isPdf(d) ? "pdf" : "image" }))
        .concat(this.uploading);
    },
  },
  watch: {
    orderId: { immediate: true, handler() { this.load(); } },
  },
  methods: {
    async load() {
      const res = await rqApi.documents(this.orderId);
      if (!res.ok) return;
      this.documents = res.body?.documents || [];
      this.otherDocuments = res.body?.other_documents || [];
    },
    isPdf(doc) {
      return (doc.mime || "").includes("pdf") || /\.pdf(\?|$)/i.test(doc.url || "");
    },
    view(doc) {
      const i = this.uploadedDocs.indexOf(doc);
      if (i >= 0) this.viewerIndex = i;
    },
    // an empty final-inspection slot can always be filled (Android DocumentAdapter)
    canUploadSlot(doc) {
      if (this.readOnly) return false;
      return this.canUpload || (doc.type === "inspection" && !doc.url);
    },
    pick(target, multiple = false) {
      this.target = target;
      this.$refs.fileInput.multiple = multiple;
      this.$refs.fileInput.click();
    },
    uploadSlot(doc) {
      if (doc.type !== "inspection") return this.pick({ kind: "slot", doc });
      this.$dialog.show({
        title: "Alert",
        text: "Are you sure you want to submit final inspection document?",
        buttonText: "Yes",
        onConfirm: () => this.pick({ kind: "slot", doc }),
      });
    },
    async onFiles(e) {
      const files = Array.from(e.target.files || []);
      e.target.value = ""; // same file can be picked again
      const target = this.target;
      if (!files.length || !target) return;
      if (files.some(f => f.size > MAX_SIZE)) return toast.error("Selected media size shouldn't be greater than 10MB.");

      if (target.kind === "slot") {
        const res = await rqApi.uploadDocument(this.orderId, target.doc.id, files[0]);
        if (!res.ok) return toast.error(res.message || "Unable to upload the document.");
        this.documents = res.body?.documents || this.documents;
        this.otherDocuments = res.body?.other_documents || this.otherDocuments;
        toast.success(res.message || "Uploaded Successfully");
        return;
      }
      // each picked file uploads on its own, titled with its file name (Android does the same)
      for (const file of files) {
        const tile = { id: "up-" + Math.random(), url: "", title: file.name, mime: "image", isLoading: true };
        this.uploading.push(tile);
        const res = await rqApi.uploadOtherDocument(this.orderId, file.name.replace(/\.[^.]+$/, ""), file);
        // by id: the array holds a reactive proxy, never === the raw tile
        this.uploading = this.uploading.filter(t => t.id !== tile.id);
        if (!res.ok) {
          toast.error(res.message || `Unable to upload ${file.name}.`);
          continue;
        }
        this.otherDocuments.push(res.body);
        toast.success(res.message || "Uploaded Successfully");
      }
    },
    remove(file) {
      this.$dialog.show({
        title: "Delete Document",
        text: "Are you sure you want to delete?",
        buttonText: "Delete",
        onConfirm: async () => {
          const res = await rqApi.removeOtherDocument(this.orderId, file.id);
          if (!res.ok) return toast.error(res.message || "Unable to delete the document.");
          this.otherDocuments = this.otherDocuments.filter(d => d.id !== file.id);
          toast.success(res.message || "Deleted Successfully");
        },
      });
    },
  },
};
</script>

<style scoped>
.rqDocBtn {
  min-width: 72px;
  height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  border-width: 1px;
  font-size: 13px;
  transition: background-color 0.2s, color 0.2s;
}
</style>
